import React, { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { AlertTriangle, CheckCircle2, Download, Printer, QrCode, RefreshCw, Settings2, Share2, Smartphone, Trash2, Wifi, Bluetooth, Cable, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { detectDevicePlatform, executePrintLaunchAction, updatePrintJobStatus } from '../../lib/printing';
import { useAuthStore } from '../../store/authStore';
import { persistence } from '../../lib/persistence';
import { WifiOff, ShieldCheck as ShieldCheckIcon } from 'lucide-react';

import type { BridgeAndroidDownloadMeta, BridgePairingTokenResponse, PrintActionResponse, PrintBridgeDevice, PrintJob, PrinterConfig, PrinterProviderMetadata } from '../../types';

const providerInstructionMap: Record<string, string[]> = {
  GARCOM_DIGITAL_BRIDGE: [
    'Auto print real fica concentrado no app bridge Android do Garcom Digital, nao no navegador.',
    'A estacao bridge cria jobs na fila e o dispositivo autenticado faz polling, claim e ack da impressao.',
    'iPhone continua fora do escopo de auto print universal; mantenha fluxo assistido para iOS.',
  ],
  STAR_PASSPRNT: [
    'Requer app PassPRNT instalado no iPhone ou Android do restaurante.',
    'Use apenas com impressora Star compativel validada pelo restaurante.',
    'O MVP abre o app do fabricante por deep link; nao existe promessa de auto-print universal no navegador.',
  ],
  BIXOLON_MPRINT: [
    'Requer app mPrint instalado e impressora BIXOLON compativel.',
    'O fluxo do MVP usa compartilhamento/abertura assistida no dispositivo movel.',
    'Valide o modelo e o comportamento no aparelho real antes da operacao comercial.',
  ],
  MANUAL_SHARE: [
    'Fallback cross-platform do MVP.',
    'A impressao depende dos destinos reais disponiveis no dispositivo: AirPrint, PDF, app de impressao ou impressora compartilhada.',
    'Nao pressupoe compatibilidade automatica com impressora termica generica via navegador.',
  ],
  EPSON_STUB: [
    'Future-ready apenas.',
    'Epson exige integracao mais especifica via SDK/app nativo.',
    'Nao e suporte garantido no MVP web/mobile.',
  ],
};

const AdminPrinting: React.FC = () => {
  const { user } = useAuthStore();
  const [providers, setProviders] = useState<PrinterProviderMetadata[]>([]);
  const [configs, setConfigs] = useState<PrinterConfig[]>([]);
  const [bridgeDevices, setBridgeDevices] = useState<PrintBridgeDevice[]>([]);
  const [bridgeAppMeta, setBridgeAppMeta] = useState<BridgeAndroidDownloadMeta | null>(null);
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [establishmentSettings, setEstablishmentSettings] = useState<Record<string, unknown>>({});
  const [form, setForm] = useState({
    name: '',
    provider: 'GARCOM_DIGITAL_BRIDGE',
    copies: '1',
    paperWidth: '58mm',
    printOnNewOrder: true,
    modelHint: '',
    bridgeDeviceId: '',
  });
  const [bridgeForm, setBridgeForm] = useState({
    name: '',
    deviceIdentifier: '',
    printerConnectionType: 'BLUETOOTH',
    printerName: '',
    printerAddress: '',
    paperWidth: '58mm',
    printerConfigId: '',
  });
  const [issuedBridgeToken, setIssuedBridgeToken] = useState('');
  const [pairingQrDataUrl, setPairingQrDataUrl] = useState('');
  const [pairingPayload, setPairingPayload] = useState<BridgePairingTokenResponse | null>(null);
  const [pairingName, setPairingName] = useState('');
  const [configActionKey, setConfigActionKey] = useState<string | null>(null);
  const [bridgeActionKey, setBridgeActionKey] = useState<string | null>(null);
  const [refreshingJobs, setRefreshingJobs] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastSync, setLastSync] = useState<string | null>(persistence.getLastSync());


  const platform = useMemo(() => detectDevicePlatform(), []);

  const loadData = useCallback(async () => {
    if (!user?.establishmentId) return;

    try {
      setLoading(true);
      const [providerResponse, configResponse, jobsResponse, establishmentResponse, bridgeDevicesResponse, bridgeMetaResult] = await Promise.all([
        api.get('/printing/providers'),
        api.get('/printer-configs'),
        api.get('/print-jobs'),
        api.get(`/establishments/id/${user.establishmentId}`),
        api.get('/print-bridge/devices'),
        api.get('/downloads/bridge-android/meta').then((response) => ({ ok: true as const, data: response.data })).catch(() => ({ ok: false as const, data: null })),
      ]);

      setProviders(providerResponse.data ?? []);
      setConfigs(configResponse.data ?? []);
      setJobs(jobsResponse.data ?? []);
      setBridgeDevices(bridgeDevicesResponse.data ?? []);
      setBridgeAppMeta(bridgeMetaResult.ok ? bridgeMetaResult.data : null);
      setEstablishmentSettings(establishmentResponse.data?.settings ?? {});
      setModuleEnabled(establishmentResponse.data?.settings?.printingModuleEnabled !== false);

      // Persistir para segurança offline
      persistence.save('PRINTERS', {
        providers: providerResponse.data ?? [],
        configs: configResponse.data ?? [],
        bridgeDevices: bridgeDevicesResponse.data ?? [],
        moduleEnabled: establishmentResponse.data?.settings?.printingModuleEnabled !== false
      });
      setLastSync(new Date().toISOString());
      setIsOffline(false);
    } catch (error) {
      console.error('Failed to load printing settings', error);
      setIsOffline(true);
      
      setMessage('Nao foi possivel carregar a area de impressao da API. Exibindo dados locais.');
    } finally {
      setLoading(false);
    }


  }, [user?.establishmentId]);

  useEffect(() => {
    // Carga inicial do cache
    const cached = persistence.load<any>('PRINTERS');
    if (cached) {
      setProviders(cached.providers || []);
      setConfigs(cached.configs || []);
      setBridgeDevices(cached.bridgeDevices || []);
      setModuleEnabled(cached.moduleEnabled !== false);
    }

    void loadData();

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData]);



  const saveModuleSetting = async (enabled: boolean) => {
    if (!user?.establishmentId) return;

    try {
      setConfigActionKey('module');
      setSaving(true);
      await api.patch(`/establishments/${user.establishmentId}`, {
        settings: {
          ...establishmentSettings,
          printingModuleEnabled: enabled,
        },
      });
      setEstablishmentSettings((prev) => ({ ...prev, printingModuleEnabled: enabled }));
      setModuleEnabled(enabled);
      setMessage(enabled ? 'Modulo de impressao ativado.' : 'Modulo de impressao desativado.');
    } catch (error) {
      console.error('Failed to save printing module setting', error);
      setMessage('Nao foi possivel atualizar o modulo de impressao.');
    } finally {
      setSaving(false);
      setConfigActionKey(null);
    }
  };

  const createConfig = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      setSaving(true);
      await api.post('/printer-configs', {
        name: form.name,
        provider: form.provider,
        copies: Number(form.copies),
        paperWidth: form.paperWidth,
        printOnNewOrder: form.printOnNewOrder,
        bridgeDeviceId: form.provider === 'GARCOM_DIGITAL_BRIDGE' ? form.bridgeDeviceId || undefined : undefined,
        providerSettings: {
          modelHint: form.modelHint,
        },
      });

      setForm({
        name: '',
        provider: 'GARCOM_DIGITAL_BRIDGE',
        copies: '1',
        paperWidth: '58mm',
        printOnNewOrder: true,
        modelHint: '',
        bridgeDeviceId: '',
      });
      setMessage('Estacao de impressao criada.');
      await loadData();
    } catch (error: any) {
      console.error('Failed to create printer config', error);
      setMessage(error?.response?.data?.message || 'Nao foi possivel criar a estacao.');
    } finally {
      setSaving(false);
    }
  };

  const patchConfig = async (configId: string, payload: Partial<PrinterConfig>, actionLabel = 'config') => {
    try {
      setConfigActionKey(`${actionLabel}:${configId}`);
      await api.patch(`/printer-configs/${configId}`, payload);
      await loadData();
    } catch (error) {
      console.error('Failed to update printer config', error);
      setMessage('Nao foi possivel atualizar a estacao.');
    } finally {
      setConfigActionKey(null);
    }
  };

  const registerBridgeDevice = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      setSaving(true);
      const { data } = await api.post('/print-bridge/devices/register', {
        name: bridgeForm.name,
        platform: 'ANDROID',
        deviceIdentifier: bridgeForm.deviceIdentifier,
        printerConnectionType: bridgeForm.printerConnectionType,
        printerName: bridgeForm.printerName || undefined,
        printerAddress: bridgeForm.printerAddress || undefined,
        paperWidth: bridgeForm.paperWidth,
        printerConfigId: bridgeForm.printerConfigId || undefined,
      });

      setIssuedBridgeToken(data.authToken || '');
      setBridgeForm({
        name: '',
        deviceIdentifier: '',
        printerConnectionType: 'BLUETOOTH',
        printerName: '',
        printerAddress: '',
        paperWidth: '58mm',
        printerConfigId: '',
      });
      setMessage('Bridge device registrado. Guarde o token no app Android bridge.');
      await loadData();
    } catch (error: any) {
      console.error('Failed to register bridge device', error);
      setMessage(error?.response?.data?.message || 'Nao foi possivel registrar o bridge device.');
    } finally {
      setSaving(false);
    }
  };

  const downloadBridgeApk = async () => {
    try {
      setBridgeActionKey('download');
      setSaving(true);
      const { data } = await api.get('/downloads/bridge-android');
      if (!data?.secureUrl) {
        throw new Error('Release ativa do Bridge App nao possui URL valida.');
      }
      window.open(data.secureUrl, '_blank', 'noopener,noreferrer');
      setMessage('APK do Bridge App baixado com sucesso.');
    } catch (error) {
      console.error('Failed to download bridge apk', error);
      setMessage('Nao foi possivel baixar o APK do Bridge App.');
    } finally {
      setSaving(false);
      setBridgeActionKey(null);
    }
  };

  const generatePairingQr = async () => {
    try {
      setBridgeActionKey('pairing');
      setSaving(true);
      const { data } = await api.post<BridgePairingTokenResponse>('/print-bridge/devices/pairing-token', {
        name: pairingName || undefined,
        paperWidth: '58mm',
        printerConnectionType: 'BLUETOOTH',
      });

      const baseUrl = String(api.defaults.baseURL || '').replace(/\/$/, '');
      const payload = JSON.stringify({
        baseUrl,
        token: data.authToken,
      });
      const qr = await QRCode.toDataURL(payload, {
        width: 280,
        margin: 1,
      });
      setPairingPayload(data);
      setPairingQrDataUrl(qr);
      setMessage('QR temporario de conexao gerado. Ele expira se nao for usado a tempo.');
      await loadData();
    } catch (error: any) {
      console.error('Failed to generate pairing QR', error);
      setMessage(error?.response?.data?.message || 'Nao foi possivel gerar o QR de vinculacao.');
    } finally {
      setSaving(false);
      setBridgeActionKey(null);
    }
  };

  const patchBridgeDevice = async (deviceId: string, payload: Record<string, unknown>) => {
    try {
      await api.patch(`/print-bridge/devices/${deviceId}`, payload);
      await loadData();
    } catch (error) {
      console.error('Failed to update bridge device', error);
      setMessage('Nao foi possivel atualizar o bridge device.');
    }
  };

  const deleteConfig = async (configId: string) => {
    if (!window.confirm('Excluir esta estacao de impressao?')) return;

    try {
      setConfigActionKey(`delete:${configId}`);
      await api.delete(`/printer-configs/${configId}`);
      setMessage('Estacao removida.');
      await loadData();
    } catch (error) {
      console.error('Failed to delete printer config', error);
      setMessage('Nao foi possivel excluir a estacao.');
    } finally {
      setConfigActionKey(null);
    }
  };

  const runAction = async (action: PrintActionResponse) => {
    try {
      if (action.launchActionType === 'none') {
        setMessage(action.userMessage);
        await loadData();
        return;
      }

      await executePrintLaunchAction(action);
      await updatePrintJobStatus(action.jobId, 'LAUNCHED', platform);

      const succeeded = window.confirm(
        `${action.userMessage}\n\nO fluxo abriu corretamente no dispositivo e o restaurante confirmou a impressao?`,
      );

      await updatePrintJobStatus(
        action.jobId,
        succeeded ? 'SUCCEEDED' : 'FAILED',
        platform,
        succeeded ? undefined : 'Fluxo iniciado, mas o usuario nao confirmou a impressao.',
      );

      setMessage(succeeded ? 'Impressao confirmada pelo operador.' : 'Tentativa registrada como falha.');
      await loadData();
    } catch (error: any) {
      console.error('Failed to execute print action', error);
      await updatePrintJobStatus(
        action.jobId,
        'FAILED',
        platform,
        error?.message || 'Falha ao lancar o provider no dispositivo.',
      );
      setMessage(error?.message || 'Nao foi possivel iniciar a impressao.');
      await loadData();
    }
  };

  const testPrint = async (configId: string) => {
    try {
      setConfigActionKey(`test:${configId}`);
      const { data } = await api.post(`/printer-configs/${configId}/test-print`, {
        devicePlatform: platform,
      });
      await runAction(data);
    } catch (error: any) {
      console.error('Failed to request test print', error);
      setMessage(error?.response?.data?.message || 'Nao foi possivel preparar o teste.');
    } finally {
      setConfigActionKey(null);
    }
  };

  const currentPlatformMessage =
    platform === 'DESKTOP'
      ? 'Voce esta em desktop. Teste final no celular/tablet real do restaurante.'
      : platform === 'IOS'
        ? 'Este dispositivo foi identificado como iPhone/iPad.'
        : platform === 'ANDROID'
          ? 'Este dispositivo foi identificado como Android.'
          : 'Plataforma nao identificada; trate o teste como assistido.';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Impressao de pedidos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            MVP mobile-first para iPhone e Android, sem prometer compatibilidade universal automatica.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isOffline ? (
            <div className="flex flex-col items-end">
              <span className="badge flex items-center gap-1.5 border-amber-500/50 bg-amber-500/10 text-amber-500">
                <WifiOff className="h-3.5 w-3.5" />
                Modo Offline de Segurança
              </span>
              {lastSync && (
                <span className="mt-1 text-[10px] text-amber-200/50">
                  Última sincronização: {new Date(lastSync).toLocaleTimeString('pt-BR')}
                </span>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <span className="badge flex items-center gap-1.5 border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                <ShieldCheckIcon className="h-3.5 w-3.5" />
                Impressão Sincronizada
              </span>
              {lastSync && (
                <span className="mt-1 text-[10px] text-emerald-400/40">
                  Configurações protegidas localmente
                </span>
              )}
            </div>
          )}
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-primary" />
              <span>{currentPlatformMessage}</span>
            </div>
          </div>
        </div>
      </div>


      {message && (
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-white/80">
          {message}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr]">
        <section className="card space-y-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-white">Modulo e estacoes</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Ative/desative o modulo e cadastre estacoes para cozinha, balcao ou expedicao.
              </p>
            </div>
            <button
              onClick={() => void saveModuleSetting(!moduleEnabled)}
              disabled={saving}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                moduleEnabled ? 'bg-primary text-white' : 'bg-[#1A1A1A] text-muted-foreground'
              }`}
            >
              {configActionKey === 'module' ? 'Atualizando...' : moduleEnabled ? 'Modulo ativo' : 'Modulo pausado'}
            </button>
          </div>

          <form onSubmit={createConfig} className="grid gap-4 md:grid-cols-2">
            <input
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              className="input"
              placeholder="Ex.: Cozinha"
              required
            />
            <select
              value={form.provider}
              onChange={(event) => setForm((prev) => ({ ...prev, provider: event.target.value }))}
              className="input"
            >
              {providers.filter((provider) => !provider.isStub).map((provider) => (
                <option key={provider.code} value={provider.code}>{provider.label}</option>
              ))}
            </select>
            <input
              value={form.copies}
              onChange={(event) => setForm((prev) => ({ ...prev, copies: event.target.value }))}
              className="input"
              placeholder="1"
              type="number"
              min="1"
              max="5"
            />
            <select
              value={form.paperWidth}
              onChange={(event) => setForm((prev) => ({ ...prev, paperWidth: event.target.value }))}
              className="input"
            >
              <option value="58mm">58mm</option>
              <option value="80mm">80mm</option>
            </select>
            <input
              value={form.modelHint}
              onChange={(event) => setForm((prev) => ({ ...prev, modelHint: event.target.value }))}
              className="input md:col-span-2"
              placeholder="Modelo/hardware validado pelo restaurante"
            />
            {form.provider === 'GARCOM_DIGITAL_BRIDGE' && (
              <select
                value={form.bridgeDeviceId}
                onChange={(event) => setForm((prev) => ({ ...prev, bridgeDeviceId: event.target.value }))}
                className="input md:col-span-2"
              >
                <option value="">Fila geral do bridge</option>
                {bridgeDevices.filter((device) => device.isActive).map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name} · {device.isOnline ? 'online' : 'offline'}
                  </option>
                ))}
              </select>
            )}
            <label className="flex items-center gap-3 text-sm text-white md:col-span-2">
              <input
                type="checkbox"
                checked={form.printOnNewOrder}
                onChange={(event) => setForm((prev) => ({ ...prev, printOnNewOrder: event.target.checked }))}
              />
              Mostrar prompt assistido para novos pedidos nesta estacao
            </label>
            <button type="submit" disabled={saving || !moduleEnabled} className="btn-primary md:col-span-2">
              {saving ? 'Salvando...' : 'Cadastrar estacao'}
            </button>
          </form>

          <div className="space-y-3">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
                Carregando estacoes...
              </div>
            ) : configs.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
                Nenhuma estacao cadastrada ainda.
              </div>
            ) : (
              configs.map((config) => (
                <div key={config.id} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-bold text-white">{config.name}</h3>
                        <span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px] text-white/70">
                          {config.providerMetadata.label}
                        </span>
                        <span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px] text-white/70">
                          {config.paperWidth}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{config.providerMetadata.description}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {config.supportedPlatforms.map((supportedPlatform) => (
                          <span key={supportedPlatform} className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] text-primary">
                            {supportedPlatform}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => void patchConfig(config.id, { isActive: !config.isActive }, 'toggle')}
                        disabled={!!configActionKey}
                        className="rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm text-white"
                      >
                        {configActionKey === `toggle:${config.id}`
                          ? config.isActive ? 'Desativando...' : 'Ativando...'
                          : config.isActive ? 'Desativar' : 'Ativar'}
                      </button>
                      <button
                        onClick={() => void testPrint(config.id)}
                        disabled={!!configActionKey}
                        className="rounded-xl border border-primary/40 px-3 py-2 text-sm text-primary"
                      >
                        {configActionKey === `test:${config.id}` ? 'Testando...' : 'Testar impressao'}
                      </button>
                      <button
                        onClick={() => void deleteConfig(config.id)}
                        disabled={!!configActionKey}
                        className="rounded-xl border border-red-500/30 px-3 py-2 text-sm text-red-300"
                      >
                        {configActionKey === `delete:${config.id}` ? 'Excluindo...' : <Trash2 className="inline h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <label className="rounded-2xl border border-[#2A2A2A] p-3 text-sm text-white">
                      <div className="flex items-center justify-between gap-3">
                        <span>Prompt em novo pedido</span>
                        <input
                          type="checkbox"
                          checked={config.printOnNewOrder}
                          disabled={!!configActionKey}
                          onChange={(event) => void patchConfig(config.id, { printOnNewOrder: event.target.checked }, 'prompt')}
                        />
                      </div>
                    </label>
                      <div className="rounded-2xl border border-[#2A2A2A] p-3 text-sm text-muted-foreground">
                      {config.provider === 'GARCOM_DIGITAL_BRIDGE'
                        ? `Bridge ${config.bridgeDevice?.name ? `vinculado a ${config.bridgeDevice.name}` : 'sem device fixo, aceitando claim de bridge online do restaurante'}.`
                        : config.providerMetadata.requiresExternalApp
                          ? 'Requer app externo do fabricante.'
                          : 'Nao requer app dedicado.'}
                      </div>
                    </div>

                  {config.provider === 'GARCOM_DIGITAL_BRIDGE' && (
                    <div className="mt-4">
                      <label className="mb-2 block text-sm font-medium text-white">Bridge device alvo</label>
                      <select
                        value={config.bridgeDeviceId || ''}
                        disabled={!!configActionKey}
                        onChange={(event) => void patchConfig(config.id, { bridgeDeviceId: event.target.value || null } as Partial<PrinterConfig>, 'bridge')}
                        className="input"
                      >
                        <option value="">Fila geral do bridge</option>
                        {bridgeDevices.filter((device) => device.isActive).map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} · {device.isOnline ? 'online' : 'offline'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mt-4 space-y-2">
                    {config.warnings.map((warning) => (
                      <div key={warning} className="flex items-start gap-2 text-xs text-amber-200">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="space-y-4">
          <div className="card p-6">
            <div className="flex items-center gap-2">
              <Download className="h-4 w-4 text-primary" />
              <h2 className="text-lg font-bold text-white">Aplicativo de Impressao Automatica</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Instale o aplicativo Bridge em um celular Android no balcao para imprimir pedidos automaticamente em impressoras termicas 58mm.
            </p>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <button onClick={() => void downloadBridgeApk()} disabled={saving || !bridgeAppMeta} className="btn-primary">
                {bridgeActionKey === 'download' ? 'Baixando APK...' : 'Baixar APK'}
              </button>
              <button onClick={() => void generatePairingQr()} disabled={saving} className="rounded-xl border border-primary/40 px-4 py-3 text-sm font-semibold text-primary">
                {bridgeActionKey === 'pairing' ? 'Gerando QR...' : 'Gerar QR de conexao'}
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input
                value={pairingName}
                onChange={(event) => setPairingName(event.target.value)}
                className="input"
                placeholder="Nome do aparelho Android do balcao"
              />
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-muted-foreground">
                O QR contem `baseUrl + token` e expira apos a geracao se nao for usado.
              </div>
            </div>

            {bridgeAppMeta ? (
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Versao</p>
                  <p className="mt-2 text-sm font-semibold text-white">{bridgeAppMeta.version} ({bridgeAppMeta.build})</p>
                </div>
                <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Tamanho</p>
                  <p className="mt-2 text-sm font-semibold text-white">{formatBytes(bridgeAppMeta.fileSize)}</p>
                </div>
                <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Android minimo</p>
                  <p className="mt-2 text-sm font-semibold text-white">{bridgeAppMeta.minAndroid}</p>
                </div>
                <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Liberado em</p>
                  <p className="mt-2 text-sm font-semibold text-white">{new Date(bridgeAppMeta.releasedAt).toLocaleString('pt-BR')}</p>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
                Nenhuma release ativa do Bridge App Android foi publicada ainda pelo painel da plataforma.
              </div>
            )}

            <div className="mt-4 grid gap-4 xl:grid-cols-[0.85fr,1.15fr]">
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <div className="flex items-center gap-2">
                  <QrCode className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold text-white">QR temporario de vinculacao</p>
                </div>
                {pairingQrDataUrl ? (
                  <div className="mt-4 space-y-3">
                    <img src={pairingQrDataUrl} alt="QR temporario do Bridge App Android" className="mx-auto w-full max-w-[280px] rounded-2xl bg-white p-3" />
                    {pairingPayload?.authTokenExpiresAt && (
                      <p className="text-xs text-amber-200">
                        Expira em: {new Date(pairingPayload.authTokenExpiresAt).toLocaleString('pt-BR')}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      O token nao volta a aparecer em texto plano depois da vinculacao. Gere outro QR se precisar instalar em outro aparelho.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
                    Gere um QR para o celular Android do balcao escanear no Bridge App.
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-sm font-semibold text-white">Como instalar</p>
                <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                  <p>1. Baixe o APK no celular Android do balcao.</p>
                  <p>2. Ative "Instalar apps desconhecidos".</p>
                  <p>3. Instale o aplicativo Bridge.</p>
                  <p>4. Abra o Bridge App.</p>
                  <p>5. Escaneie o QR Code do painel.</p>
                  <p>6. Conecte a impressora Bluetooth 58mm.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6">
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-primary" />
            <h2 className="text-lg font-bold text-white">Providers do MVP</h2>
            </div>
            <div className="mt-4 space-y-4">
              {providers.map((provider) => (
                <div key={provider.code} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">{provider.label}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{provider.description}</p>
                    </div>
                    <span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px] text-white/70">
                      {provider.code}
                    </span>
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    {(providerInstructionMap[provider.code] || []).map((instruction) => (
                      <div key={instruction} className="flex items-start gap-2">
                        {provider.code === 'MANUAL_SHARE' ? <Share2 className="mt-0.5 h-4 w-4" /> : <Printer className="mt-0.5 h-4 w-4" />}
                        <span>{instruction}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-primary" />
                <h2 className="text-lg font-bold text-white">Ultimos print jobs</h2>
              </div>
              <button
                onClick={async () => {
                  try {
                    setRefreshingJobs(true);
                    await loadData();
                  } finally {
                    setRefreshingJobs(false);
                  }
                }}
                disabled={refreshingJobs}
                className="rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {refreshingJobs ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {jobs.slice(0, 8).map((job) => (
                <div key={job.id} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{job.printerConfig?.name || 'Estacao'}</p>
                      <p className="text-xs text-muted-foreground">{job.provider} · {job.triggerSource}</p>
                      {job.targetBridgeDevice && (
                        <p className="mt-1 text-[11px] text-muted-foreground">Bridge alvo: {job.targetBridgeDevice.name}</p>
                      )}
                      {job.claimedByBridgeDevice && (
                        <p className="mt-1 text-[11px] text-muted-foreground">Claim por: {job.claimedByBridgeDevice.name}</p>
                      )}
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] ${
                      job.status === 'SUCCEEDED'
                        ? 'bg-emerald-500/12 text-emerald-300'
                        : job.status === 'FAILED'
                          ? 'bg-red-500/12 text-red-300'
                          : 'bg-yellow-500/12 text-yellow-300'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {new Date(job.createdAt).toLocaleString('pt-BR')}
                  </p>
                  {job.errorMessage && (
                    <p className="mt-2 text-xs text-red-300">{job.errorMessage}</p>
                  )}
                  {job.failureReason && job.failureReason !== job.errorMessage && (
                    <p className="mt-2 text-xs text-red-300">{job.failureReason}</p>
                  )}
                </div>
              ))}
              {jobs.length === 0 && !loading && (
                <div className="rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
                  Nenhum print job registrado ainda.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5 text-sm text-amber-50">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4" />
              <div>
                O MVP registra preparo, lancamento e confirmacao assistida pelo operador. Ele nao tenta auto-print silencioso no navegador mobile.
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h2 className="text-lg font-bold text-white">Bridge Android</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              O auto print real fica concentrado no app bridge Android do Garcom Digital. O navegador apenas cria a fila e acompanha status.
            </p>

            <form onSubmit={registerBridgeDevice} className="mt-4 grid gap-3">
              <input
                value={bridgeForm.name}
                onChange={(event) => setBridgeForm((prev) => ({ ...prev, name: event.target.value }))}
                className="input"
                placeholder="Ex.: Android cozinha"
                required
              />
              <input
                value={bridgeForm.deviceIdentifier}
                onChange={(event) => setBridgeForm((prev) => ({ ...prev, deviceIdentifier: event.target.value }))}
                className="input"
                placeholder="Identificador unico do aparelho"
                required
              />
              <div className="grid gap-3 md:grid-cols-2">
                <select
                  value={bridgeForm.printerConnectionType}
                  onChange={(event) => setBridgeForm((prev) => ({ ...prev, printerConnectionType: event.target.value }))}
                  className="input"
                >
                  <option value="BLUETOOTH">Bluetooth</option>
                  <option value="WIFI">Wi-Fi</option>
                  <option value="USB">USB</option>
                </select>
                <select
                  value={bridgeForm.paperWidth}
                  onChange={(event) => setBridgeForm((prev) => ({ ...prev, paperWidth: event.target.value }))}
                  className="input"
                >
                  <option value="58mm">58mm</option>
                  <option value="80mm">80mm</option>
                </select>
              </div>
              <input
                value={bridgeForm.printerName}
                onChange={(event) => setBridgeForm((prev) => ({ ...prev, printerName: event.target.value }))}
                className="input"
                placeholder="Nome da impressora"
              />
              <input
                value={bridgeForm.printerAddress}
                onChange={(event) => setBridgeForm((prev) => ({ ...prev, printerAddress: event.target.value }))}
                className="input"
                placeholder="Endereco Bluetooth ou IP da impressora"
              />
              <select
                value={bridgeForm.printerConfigId}
                onChange={(event) => setBridgeForm((prev) => ({ ...prev, printerConfigId: event.target.value }))}
                className="input"
              >
                <option value="">Vincular estacao bridge depois</option>
                {configs.filter((config) => config.provider === 'GARCOM_DIGITAL_BRIDGE').map((config) => (
                  <option key={config.id} value={config.id}>{config.name}</option>
                ))}
              </select>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Registrando...' : 'Registrar bridge device'}
              </button>
            </form>

            {issuedBridgeToken && (
              <div className="mt-4 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-50">
                <p className="font-semibold">Token gerado para o app bridge</p>
                <p className="mt-2 break-all font-mono text-xs">{issuedBridgeToken}</p>
                <p className="mt-2 text-xs text-amber-100/80">
                  Mostre esse token apenas uma vez no app Android bridge. Depois disso, o backend guarda somente o hash.
                </p>
              </div>
            )}

            <div className="mt-4 space-y-3">
              {bridgeDevices.map((device) => (
                <div key={device.id} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-white">{device.name}</p>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] ${device.isOnline ? 'bg-emerald-500/12 text-emerald-300' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                          {device.isOnline ? 'Online' : 'Offline'}
                        </span>
                        <span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px] text-white/70">
                          {device.platform}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Ultimo heartbeat: {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString('pt-BR') : 'Nunca'}
                      </p>
                      {device.lastPrintJob && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Ultimo print job: {device.lastPrintJob.status}
                          {device.lastPrintJob.orderShortId ? ` · Pedido ${device.lastPrintJob.orderShortId}` : ''}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {device.deviceIdentifier}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Vinculos: {device.printerConfigs?.length ? device.printerConfigs.map((config) => config.name).join(', ') : 'Nenhuma estacao vinculada'}
                      </p>
                    </div>
                    <button
                      onClick={() => void patchBridgeDevice(device.id, { isActive: !device.isActive })}
                      className="rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm text-white"
                    >
                      {device.isActive ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      {device.printerConnectionType === 'BLUETOOTH' ? <Bluetooth className="h-3.5 w-3.5" /> : device.printerConnectionType === 'WIFI' ? <Wifi className="h-3.5 w-3.5" /> : <Cable className="h-3.5 w-3.5" />}
                      {device.printerConnectionType}
                    </span>
                    <span>{device.paperWidth}</span>
                    {device.printerName && <span>{device.printerName}</span>}
                    {device.printerAddress && <span>{device.printerAddress}</span>}
                  </div>
                </div>
              ))}
              {bridgeDevices.length === 0 && !loading && (
                <div className="rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
                  Nenhum bridge device registrado ainda.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminPrinting;

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const power = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / (1024 ** power);
  return `${size.toFixed(power === 0 ? 0 : 1)} ${units[power]}`;
}
